import { useState, useCallback, useEffect, useRef } from 'react';
import { auth } from '../firebase';
import { getMyNetworkPosts, getMyNetworkPermissions, getMyCollaborationRequests } from './networkApi';

export interface NetworkBadgeCounts {
  pendingApprovals: number;
  receivedRequests: number;
  acceptedRequests: number;
  activeCollaborations: number;
  total: number;
}

const EMPTY: NetworkBadgeCounts = {
  pendingApprovals: 0,
  receivedRequests: 0,
  acceptedRequests: 0,
  activeCollaborations: 0,
  total: 0,
};

let cachedCounts: NetworkBadgeCounts = { ...EMPTY };
const listeners = new Set<(c: NetworkBadgeCounts) => void>();

function notify() {
  listeners.forEach(fn => fn(cachedCounts));
}

export function useNetworkNotifications() {
  const [counts, setCounts] = useState<NetworkBadgeCounts>(cachedCounts);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refresh = useCallback(async () => {
    if (!auth.currentUser) return;
    setLoading(true);
    try {
      const [posts, perms, receivedRes, sentRes] = await Promise.all([
        getMyNetworkPosts(),
        getMyNetworkPermissions(),
        getMyCollaborationRequests({ direction: 'received', page: 1, pageSize: 100 }),
        getMyCollaborationRequests({ direction: 'sent', page: 1, pageSize: 100 }),
      ]);

      const canReview = perms?.canReviewNetworkPosts ?? false;
      const pendingApprovals = canReview
        ? posts.filter(p => p.status === 'pending_approval').length
        : 0;

      const receivedPending = receivedRes.items.filter(r => r.status === 'pending').length;
      const receivedAccepted = receivedRes.items.filter(r => r.status === 'approved').length;
      const sentApproved = sentRes.items.filter(r => r.status === 'approved').length;

      cachedCounts = {
        pendingApprovals,
        receivedRequests: receivedPending,
        acceptedRequests: receivedAccepted,
        activeCollaborations: sentApproved,
        total: pendingApprovals + receivedPending + receivedAccepted + sentApproved,
      };
      notify();
    } catch {
      // silently fail — badges are non-critical
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const listener = (c: NetworkBadgeCounts) => setCounts(c);
    listeners.add(listener);
    setCounts(cachedCounts);

    // Initial load if we have a user
    if (auth.currentUser && cachedCounts.total === 0) {
      refresh();
    }

    // Poll every 60 seconds
    if (auth.currentUser) {
      timerRef.current = setInterval(refresh, 60_000);
    }

    return () => {
      listeners.delete(listener);
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [refresh]);

  const markSeen = useCallback((key: keyof NetworkBadgeCounts) => {
    cachedCounts = { ...cachedCounts, [key]: 0 };
    cachedCounts.total =
      cachedCounts.pendingApprovals +
      cachedCounts.receivedRequests +
      cachedCounts.acceptedRequests +
      cachedCounts.activeCollaborations;
    notify();
  }, []);

  return { counts, loading, refresh, markSeen };
}
